// 4000px @ 0.92 keeps claim-evidence detail (reviewers zoom into
// cracks/defects) while bounding upload size. Shared by olive + iris.
const MAX_DIMENSION = 4000
const JPEG_QUALITY = 0.92
// Below this size compression saves nothing worth the CPU.
const SKIP_BELOW_BYTES = 1024 * 1024

// Formats we can safely re-encode. GIFs (animation) and SVGs are excluded.
const COMPRESSIBLE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
])

async function decodeBitmap(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    // Older Safari rejects the options bag.
    return createImageBitmap(file)
  }
}

/**
 * Downscale + re-encode a photo to JPEG before upload. Falls back to
 * the original file when the format is unsupported, decoding fails,
 * or compression doesn't actually shrink it.
 */
export async function compressImage(file: File): Promise<File> {
  if (!COMPRESSIBLE_TYPES.has(file.type) || file.size <= SKIP_BELOW_BYTES) {
    return file
  }

  // ImageBitmap holds GPU memory; close it even when canvas ops throw.
  let bitmap: ImageBitmap | null = null
  try {
    bitmap = await decodeBitmap(file)
    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height))

    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))

    const ctx = canvas.getContext('2d')
    if (!ctx) return file

    // JPEG has no alpha; flatten transparency onto white.
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()
    bitmap = null

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    )
    if (!blob || blob.size >= file.size) return file

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo'
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' })
  } catch {
    return file
  } finally {
    bitmap?.close()
  }
}
